import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { ArrowLeft, Edit2, Save, X, ArrowLeftRight, QrCode, Clock, Wrench, Upload, RefreshCw, Camera, CameraOff, CheckCircle, AlertCircle } from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import jsQR from 'jsqr';
import api from '../../services/api';
import { StatusBadge, TIPO_MOV_CONFIG } from './estoque.utils';
import { useObras, useTensoes, useEstoquePermissions } from './useEstoqueConfig';
import EstoqueMovimentarModal from './EstoqueMovimentarModal';

const TABS = ['Dados', 'QR Code', 'Histórico', 'Manutenção'];

function TrocarQRModal({ item, onClose, onSuccess }) {
  const [camera, setCamera] = useState(false);
  const [scanAtivo, setScanAtivo] = useState(false);
  const [candidato, setCandidato] = useState(null); // { token, numero_etiqueta }
  const [erro, setErro] = useState('');
  const [confirmando, setConfirmando] = useState(false);
  const [tokenManual, setTokenManual] = useState('');
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const intervalRef = useRef(null);

  const pararCamera = useCallback(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop());
    setCamera(false);
    setScanAtivo(false);
  }, []);

  useEffect(() => () => pararCamera(), [pararCamera]);

  const iniciarCamera = useCallback(async () => {
    setErro('');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
      streamRef.current = stream;
      if (videoRef.current) { videoRef.current.srcObject = stream; await videoRef.current.play(); }
      setCamera(true);
      setScanAtivo(true);
    } catch { setErro('Não foi possível acessar a câmera'); }
  }, []);

  useEffect(() => {
    if (!scanAtivo || !videoRef.current || !canvasRef.current) return;
    const interval = setInterval(async () => {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      if (!video || video.readyState !== 4) return;
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(video, 0, 0);
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const qr = jsQR(imageData.data, imageData.width, imageData.height);
      if (qr?.data) {
        const match = qr.data.match(/token=([a-f0-9-]{36})/);
        if (match) {
          clearInterval(interval);
          setScanAtivo(false);
          await validarToken(match[1]);
        }
      }
    }, 300);
    intervalRef.current = interval;
    return () => clearInterval(interval);
  }, [scanAtivo]);

  async function validarToken(token) {
    setErro('');
    if (token === item.qrcode_token) {
      setErro('Este é o QR Code atual da ferramenta. Escaneie um diferente.');
      setScanAtivo(camera);
      return;
    }
    try {
      const { data } = await api.get(`/estoque/itens/qr/${token}`);
      if (data.status === 'vinculado') {
        setErro('Este QR Code já está vinculado a outra ferramenta.');
        setScanAtivo(camera);
        return;
      }
      if (data.status === 'pregerado') {
        setCandidato({ token, numero_etiqueta: data.numero_etiqueta });
        return;
      }
      setErro('QR Code não reconhecido no sistema. Use apenas etiquetas geradas pelo Gerador de QR.');
      setScanAtivo(camera);
    } catch (e) {
      if (e.response?.status === 404) {
        setErro('QR Code não encontrado no sistema. Use apenas etiquetas geradas pelo Gerador de QR.');
      } else {
        setErro('Erro ao validar QR Code.');
      }
      setScanAtivo(camera);
    }
  }

  async function confirmarTroca() {
    if (!candidato) return;
    setConfirmando(true);
    try {
      await api.post(`/estoque/itens/${item.id}/trocar-qr`, { novo_token: candidato.token });
      onSuccess();
      onClose();
    } catch (e) {
      setErro(e.response?.data?.error || 'Erro ao trocar QR Code');
      setConfirmando(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="p-5 border-b flex items-center justify-between">
          <div className="flex items-center gap-2">
            <RefreshCw size={18} className="text-violet-600" />
            <h2 className="font-bold text-gray-800">Trocar QR Code</h2>
          </div>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-gray-100"><X size={18} /></button>
        </div>

        <div className="p-5 space-y-4">
          {/* Item atual */}
          <div className="bg-gray-50 rounded-xl p-3 text-sm">
            <p className="text-xs text-gray-500 mb-1">Ferramenta</p>
            <p className="font-semibold text-gray-800">{item.nome}</p>
            <p className="text-gray-500">{item.codigo}</p>
          </div>

          {!candidato ? (
            <>
              <p className="text-sm text-gray-600">
                Escaneie a <strong>nova placa de metal</strong> que será vinculada a esta ferramenta.
                O QR Code anterior será desvinculado automaticamente.
              </p>

              {/* Câmera */}
              <div className="border border-gray-200 rounded-xl overflow-hidden">
                <div className="p-3 flex items-center justify-between bg-gray-50 border-b">
                  <span className="text-sm font-medium text-gray-700">Escanear com câmera</span>
                  <button onClick={camera ? pararCamera : iniciarCamera}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold
                      ${camera ? 'bg-red-50 text-red-600 hover:bg-red-100' : 'bg-blue-50 text-blue-600 hover:bg-blue-100'}`}>
                    {camera ? <><CameraOff size={12} /> Fechar</> : <><Camera size={12} /> Abrir câmera</>}
                  </button>
                </div>
                {camera && (
                  <div className="relative bg-black">
                    <video ref={videoRef} playsInline muted className="w-full max-h-52 object-cover" />
                    <canvas ref={canvasRef} className="hidden" />
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                      <div className="w-36 h-36 border-2 border-white rounded-xl opacity-70" />
                    </div>
                    {!scanAtivo && (
                      <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                        <button onClick={() => { setErro(''); setScanAtivo(true); }}
                          className="bg-white text-gray-800 px-4 py-2 rounded-lg text-sm font-semibold">
                          Continuar leitura
                        </button>
                      </div>
                    )}
                    {scanAtivo && (
                      <p className="absolute bottom-2 left-0 right-0 text-center text-white text-xs opacity-80">
                        Aponte para a nova placa de metal
                      </p>
                    )}
                  </div>
                )}
              </div>

              {/* Token manual */}
              <div>
                <p className="text-xs font-medium text-gray-500 mb-2">Ou insira o token manualmente</p>
                <div className="flex gap-2">
                  <input value={tokenManual} onChange={e => setTokenManual(e.target.value.trim())}
                    placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                    className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-xs font-mono focus:outline-none focus:ring-2 focus:ring-violet-400" />
                  <button onClick={() => validarToken(tokenManual)} disabled={!tokenManual}
                    className="px-3 py-2 bg-violet-600 text-white rounded-lg text-sm font-semibold hover:bg-violet-700 disabled:opacity-40">
                    Validar
                  </button>
                </div>
              </div>

              {erro && (
                <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-xl px-3 py-2.5 text-sm text-red-700">
                  <AlertCircle size={15} className="mt-0.5 flex-shrink-0" /> {erro}
                </div>
              )}
            </>
          ) : (
            /* Confirmação */
            <div className="space-y-4">
              <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-xl px-3 py-2.5 text-sm text-green-700">
                <CheckCircle size={15} className="flex-shrink-0" /> QR Code validado com sucesso
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-center">
                  <p className="text-xs font-semibold text-red-500 mb-2">QR ATUAL (será desvinculado)</p>
                  <QRCodeSVG value={`${window.location.origin}/estoque/scanner?token=${item.qrcode_token}`} size={80} className="mx-auto" />
                  <p className="text-xs text-red-700 font-mono mt-2 break-all">{item.qrcode_token?.slice(0, 8)}...</p>
                </div>
                <div className="bg-green-50 border border-green-200 rounded-xl p-3 text-center">
                  <p className="text-xs font-semibold text-green-600 mb-2">NOVO QR (será vinculado)</p>
                  <QRCodeSVG value={`${window.location.origin}/estoque/scanner?token=${candidato.token}`} size={80} className="mx-auto" />
                  <p className="text-xs text-green-800 font-bold mt-2">{candidato.numero_etiqueta}</p>
                </div>
              </div>

              {erro && (
                <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-xl px-3 py-2.5 text-sm text-red-700">
                  <AlertCircle size={15} className="mt-0.5 flex-shrink-0" /> {erro}
                </div>
              )}

              <div className="flex gap-2 pt-1">
                <button onClick={() => { setCandidato(null); setErro(''); }}
                  className="flex-1 py-2.5 border border-gray-200 text-gray-600 rounded-xl text-sm font-medium hover:bg-gray-50">
                  Escanear outro
                </button>
                <button onClick={confirmarTroca} disabled={confirmando}
                  className="flex-1 py-2.5 bg-violet-600 text-white rounded-xl text-sm font-bold hover:bg-violet-700 disabled:opacity-50">
                  {confirmando ? 'Trocando...' : 'Confirmar Troca'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function EstoqueItem() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [tab, setTab] = useState('Dados');
  const [editando, setEditando] = useState(false);
  const [form, setForm] = useState({});
  const [movOpen, setMovOpen] = useState(false);
  const [trocarQROpen, setTrocarQROpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const { data: resp, isLoading } = useQuery({
    queryKey: ['estoque-item', id],
    queryFn: () => api.get(`/estoque/itens/${id}`).then(r => r.data.data),
  });

  const { data: cats } = useQuery({ queryKey: ['estoque-cats'], queryFn: () => api.get('/estoque/categorias').then(r => r.data.data) });
  const obras = useObras();
  const tensoes = useTensoes();
  const perm = useEstoquePermissions();

  const item = resp;

  function iniciarEdicao() {
    setForm({ ...item });
    setEditando(true);
  }

  async function salvar() {
    setSaving(true);
    try {
      await api.put(`/estoque/itens/${id}`, form);
      qc.invalidateQueries(['estoque-item', id]);
      setEditando(false);
    } catch (e) {
      alert(e.response?.data?.error || 'Erro ao salvar');
    } finally { setSaving(false); }
  }

  async function uploadFoto(e) {
    const file = e.target.files[0];
    if (!file) return;
    const fd = new FormData();
    fd.append('foto', file);
    try {
      await api.post(`/estoque/itens/${id}/foto`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      qc.invalidateQueries(['estoque-item', id]);
    } catch (_) {}
  }

  function imprimirEtiqueta() {
    const qrUrl = `${window.location.origin}/estoque/scanner?token=${item.qrcode_token}`;
    const w = window.open('', '_blank', 'width=400,height=500');
    w.document.write(`<!DOCTYPE html><html><head><title>${item.codigo}</title>
    <style>
      body{font-family:Arial,sans-serif;margin:0;padding:10px;display:flex;flex-direction:column;align-items:center;justify-content:center;}
      .logo{font-size:9px;color:#888;margin-bottom:3px;text-transform:uppercase;letter-spacing:1px;}
      .qr{width:130px;height:130px;margin:4px 0;}
      .codigo{font-size:13px;font-weight:bold;color:#1B3F5E;margin-top:4px;}
      .nome{font-size:9px;color:#555;text-align:center;max-width:170px;margin-top:2px;line-height:1.3;}
      @media print{@page{size:60mm 40mm;margin:0;}body{padding:3mm;min-height:100%;}}
    </style></head><body>
    <div class="logo">Inbase Construtora</div>
    <img src="${window.location.origin}/api/estoque/qrcode/${item.qrcode_token}/imagem" class="qr" />
    <div class="codigo">${item.codigo}</div>
    <div class="nome">${item.nome}</div>
    <script>window.onload=()=>{setTimeout(()=>{window.print();},600);}</script>
    </body></html>`);
    w.document.close();
  }

  if (isLoading) return <div className="p-6 text-gray-400">Carregando...</div>;
  if (!item) return <div className="p-6 text-red-500">Item não encontrado</div>;

  const qrUrl = `${window.location.origin}/estoque/scanner?token=${item.qrcode_token}`;
  const historico = item.historico || [];

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-4">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button onClick={() => navigate('/estoque/itens')} className="text-gray-400 hover:text-gray-600 p-1 rounded-lg">
          <ArrowLeft size={20} />
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-mono text-sm font-bold text-gray-500">{item.codigo}</span>
            <StatusBadge status={item.status} />
          </div>
          <h1 className="text-xl font-bold text-gray-800 truncate">{item.nome}</h1>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setMovOpen(true)}
            className="flex items-center gap-2 px-3 py-2 border border-gray-200 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-50">
            <ArrowLeftRight size={15} /> Movimentar
          </button>
          {perm.novo && !editando && (
            <button onClick={iniciarEdicao}
              className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-xl text-sm font-medium hover:bg-blue-700">
              <Edit2 size={15} /> Editar
            </button>
          )}
          {editando && (
            <>
              <button onClick={() => setEditando(false)} className="px-3 py-2 border border-gray-200 rounded-xl text-sm font-medium text-gray-600 hover:bg-gray-50"><X size={15} /></button>
              <button onClick={salvar} disabled={saving}
                className="flex items-center gap-2 px-3 py-2 bg-green-600 text-white rounded-xl text-sm font-medium hover:bg-green-700 disabled:opacity-50">
                <Save size={15} /> {saving ? 'Salvando...' : 'Salvar'}
              </button>
            </>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 p-1 rounded-xl w-fit">
        {TABS.map(t => (
          <button key={t} onClick={() => setTab(t)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${tab === t ? 'bg-white shadow text-gray-800' : 'text-gray-500 hover:text-gray-700'}`}>
            {t}
          </button>
        ))}
      </div>

      {/* Tab: Dados */}
      {tab === 'Dados' && (
        <div className="bg-white rounded-xl shadow p-6 space-y-4">
          <div className="flex gap-4">
            {/* Foto */}
            <div className="flex-shrink-0">
              <div className="w-28 h-28 bg-gray-100 rounded-xl overflow-hidden flex items-center justify-center border border-gray-200">
                {item.foto_url ? (
                  <img src={item.foto_url} alt={item.nome} className="w-full h-full object-cover" />
                ) : (
                  <Wrench size={32} className="text-gray-300" />
                )}
              </div>
              <label className="mt-2 flex items-center gap-1 cursor-pointer text-xs text-blue-600 hover:underline">
                <Upload size={12} /> Alterar foto
                <input type="file" accept="image/*" className="hidden" onChange={uploadFoto} />
              </label>
            </div>
            <div className="flex-1 grid grid-cols-2 gap-4">
              <Field label="Nome" value={form.nome ?? item.nome} editando={editando} onChange={v => setForm(f => ({...f, nome: v}))} />
              <Field label="Marca" value={form.marca ?? item.marca} editando={editando} onChange={v => setForm(f => ({...f, marca: v}))} />
              <Field label="Modelo" value={form.modelo ?? item.modelo} editando={editando} onChange={v => setForm(f => ({...f, modelo: v}))} />
              <Field label="Nº de Série" value={form.numero_serie ?? item.numero_serie} editando={editando} onChange={v => setForm(f => ({...f, numero_serie: v}))} />
              <Field label="Tensão" value={form.tensao ?? item.tensao} editando={editando} onChange={v => setForm(f => ({...f, tensao: v}))} type="select" options={tensoes} />
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Obra / Local</label>
                {editando ? (
                  <select value={form.obra_atual ?? item.obra_atual} onChange={e => setForm(f => ({...f, obra_atual: e.target.value}))}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm">
                    <option value="">—</option>
                    {obras.map(o => <option key={o} value={o}>{o}</option>)}
                  </select>
                ) : <p className="text-sm text-gray-800">{item.obra_atual || '—'}</p>}
              </div>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-4 pt-2 border-t">
            <Field label="Valor estimado (R$)" value={form.valor_estimado ?? item.valor_estimado} editando={editando} onChange={v => setForm(f => ({...f, valor_estimado: v}))} type="number" />
            <Field label="Data de aquisição" value={form.data_aquisicao ?? item.data_aquisicao} editando={editando} onChange={v => setForm(f => ({...f, data_aquisicao: v}))} type="date" />
            <Field label="Intervalo manutenção (dias)" value={form.intervalo_manutencao_dias ?? item.intervalo_manutencao_dias} editando={editando} onChange={v => setForm(f => ({...f, intervalo_manutencao_dias: v}))} type="number" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Observações</label>
            {editando ? (
              <textarea value={form.observacoes ?? item.observacoes ?? ''} onChange={e => setForm(f => ({...f, observacoes: e.target.value}))} rows={2}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" />
            ) : <p className="text-sm text-gray-700">{item.observacoes || '—'}</p>}
          </div>
        </div>
      )}

      {/* Tab: QR Code */}
      {tab === 'QR Code' && (
        <div className="bg-white rounded-xl shadow p-6 flex flex-col items-center gap-4">
          <QRCodeSVG value={qrUrl} size={200} />
          <div className="text-center">
            <p className="font-mono text-lg font-bold text-gray-700">{item.codigo}</p>
            <p className="text-sm text-gray-500 max-w-xs text-center">{item.nome}</p>
          </div>
          <div className="flex flex-wrap justify-center gap-3">
            <button onClick={imprimirEtiqueta}
              className="flex items-center gap-2 px-4 py-2 border border-gray-200 text-gray-700 rounded-xl text-sm font-medium hover:bg-gray-50">
              <QrCode size={16} /> Imprimir Etiqueta
            </button>
            <a href={`/api/estoque/qrcode/${item.qrcode_token}/imagem`} download={`${item.codigo}.png`}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-xl text-sm font-medium hover:bg-blue-700">
              Download PNG
            </a>
            {perm.trocar_qr && (
              <button onClick={() => setTrocarQROpen(true)}
                className="flex items-center gap-2 px-4 py-2 bg-violet-600 text-white rounded-xl text-sm font-medium hover:bg-violet-700">
                <RefreshCw size={16} /> Trocar QR Code
              </button>
            )}
          </div>
          {perm.trocar_qr && (
            <p className="text-xs text-gray-400 text-center max-w-xs">
              Use "Trocar QR Code" para substituir a placa de metal gravada caso ela seja danificada ou perdida.
            </p>
          )}
        </div>
      )}

      {/* Tab: Histórico */}
      {tab === 'Histórico' && (
        <div className="bg-white rounded-xl shadow p-6">
          <div className="relative">
            <div className="absolute left-5 top-0 bottom-0 w-0.5 bg-gray-100" />
            <div className="space-y-4">
              {historico.length === 0 && <p className="text-gray-400 text-sm pl-10">Sem movimentações</p>}
              {historico.map(m => {
                const cfg = TIPO_MOV_CONFIG[m.tipo] || { label: m.tipo, color: 'text-gray-500' };
                return (
                  <div key={m.id} className="flex gap-4">
                    <div className="w-10 h-10 bg-white border-2 border-gray-200 rounded-full flex items-center justify-center flex-shrink-0 z-10">
                      <Clock size={16} className="text-gray-400" />
                    </div>
                    <div className="flex-1 pb-2">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`text-xs font-semibold ${cfg.color}`}>{cfg.label}</span>
                        {m.obra_destino && <span className="text-xs text-gray-500">→ {m.obra_destino}</span>}
                      </div>
                      <p className="text-sm text-gray-700 font-medium">{m.responsavel}</p>
                      {m.observacao && <p className="text-xs text-gray-400">{m.observacao}</p>}
                      <p className="text-xs text-gray-400">{format(new Date(m.created_at), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Tab: Manutenção */}
      {tab === 'Manutenção' && (
        <div className="bg-white rounded-xl shadow p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="p-4 bg-gray-50 rounded-xl">
              <p className="text-xs font-medium text-gray-500 mb-1">Última manutenção</p>
              <p className="text-lg font-bold text-gray-800">
                {item.data_ultima_manutencao ? format(new Date(item.data_ultima_manutencao), 'dd/MM/yyyy', { locale: ptBR }) : 'Nunca'}
              </p>
            </div>
            <div className="p-4 bg-gray-50 rounded-xl">
              <p className="text-xs font-medium text-gray-500 mb-1">Intervalo</p>
              <p className="text-lg font-bold text-gray-800">{item.intervalo_manutencao_dias} dias</p>
            </div>
            {item.data_ultima_manutencao && (
              <div className={`p-4 rounded-xl col-span-2 ${
                new Date(item.data_ultima_manutencao).getTime() + item.intervalo_manutencao_dias * 86400000 < Date.now()
                  ? 'bg-red-50 border border-red-200' : 'bg-green-50 border border-green-200'
              }`}>
                <p className="text-xs font-medium text-gray-500 mb-1">Próxima manutenção prevista</p>
                <p className="text-lg font-bold">
                  {format(new Date(new Date(item.data_ultima_manutencao).getTime() + item.intervalo_manutencao_dias * 86400000), 'dd/MM/yyyy', { locale: ptBR })}
                </p>
              </div>
            )}
          </div>
          <button onClick={() => setMovOpen(true)}
            className="flex items-center gap-2 px-4 py-2 bg-yellow-500 text-white rounded-xl text-sm font-medium hover:bg-yellow-600">
            <Wrench size={16} /> Registrar Manutenção
          </button>
        </div>
      )}

      {movOpen && (
        <EstoqueMovimentarModal
          item={item}
          onClose={() => setMovOpen(false)}
          onSuccess={() => qc.invalidateQueries(['estoque-item', id])}
        />
      )}

      {trocarQROpen && (
        <TrocarQRModal
          item={item}
          onClose={() => setTrocarQROpen(false)}
          onSuccess={() => { qc.invalidateQueries(['estoque-item', id]); setTab('QR Code'); }}
        />
      )}
    </div>
  );
}

function Field({ label, value, editando, onChange, type = 'text', options = [] }) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-500 mb-1">{label}</label>
      {editando ? (
        type === 'select' ? (
          <select value={value ?? ''} onChange={e => onChange(e.target.value)}
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm">
            <option value="">—</option>
            {options.map(o => <option key={o} value={o}>{o}</option>)}
          </select>
        ) : (
          <input type={type} value={value ?? ''} onChange={e => onChange(e.target.value)}
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" />
        )
      ) : (
        <p className="text-sm text-gray-800">{value ?? '—'}</p>
      )}
    </div>
  );
}
